import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { boolean, date, object, SchemaOf, string, number } from "yup";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LBtn } from "../../../common/components/LBtn";
import { LInputField } from "../../../common/components/LInputField";
import { DefaultErrorMessages } from "../../../common/helpers/formValidationHelper";
import { NavItems } from "../../../common/helpers/navigation";
import { Organization } from "../../../common/helpers/organizationHelper";
import { updateOrganization } from "../../../common/store/organizationSlice";
import { SignupContainer } from "../components/SignupContainer";
import { StateSelectOptions } from "../components/StateSelectOptions";
import { CountrySelectOptions } from "../components/CountrySelectOptions";
import { useEffect } from "react";

interface OrganizationSchema extends Organization {
    otherCountry?: string;
}

const formSchema: SchemaOf<Omit<OrganizationSchema, "userId" | "primaryContact" | "administrator" | "id" | "status">> =
    object({
        npiNumber: string()
            .when("address.country", (val) => {
                if (val === "United States") {
                    return string()
                        .required(DefaultErrorMessages.required)
                        .test("len", "NPI number must be at least 10 digits", (len) => len?.length === 10);
                }
                return string()
                    .optional()
                    .test(
                        "len",
                        "NPI number must be at least 10 digits",
                        (len) => len?.length === 0 || len?.length === 10
                    );
            })
            .matches(/^[0-9]+$/, {
                message: "NPI Number must be numeric",
                excludeEmptyString: true
            }),
        orgName: string().required(DefaultErrorMessages.required),
        address: object({
            streetAddress: string().required(DefaultErrorMessages.required),
            streetAddress2: string().optional(),
            city: string().required(DefaultErrorMessages.required),
            stateCode: string().required(DefaultErrorMessages.required),
            postalCode: string()
                .length(5, "Postal code must be 5 digits")
                .matches(/^[0-9]{5}/, "Postal code must be numeric")
                .required(DefaultErrorMessages.required),
            country: string().required()
        }).required(),
        agreeTerms: boolean().optional(),
        agreeTermsStamp: date().optional(),
        activeStaffUsers: number().optional(),
        inactiveStaffUsers: number().optional(),
        details: string().optional(),
        otherCountry: string().when("address.country", (val, schema) => {
            return val === "Other" ? schema.required("Other country is required") : schema;
        }),
        locationId: number().optional()
    });

export const SignupOrgInfo = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const validateMethods = useForm<Organization>({
        resolver: yupResolver(formSchema)
    });
    const { organization } = useAppSelector((x) => {
        return { organization: x.organization };
    });

    const formSubmitHandler: SubmitHandler<Organization> = (data: OrganizationSchema) => {
        data.address.country = data.address.country === "Other" ? data.otherCountry : data.address.country;

        dispatch(updateOrganization(data));
        navigate(NavItems.Signup.Terms.path, { state: { orgAdmin: true } });
    };

    useEffect(() => {
        if (!organization.address.country) {
            validateMethods.setValue("address.country", "United States");
        }
    }, []);

    const country = validateMethods.watch("address.country");

    return (
        <FormProvider {...validateMethods}>
            <SignupContainer
                title="Main Contact Information"
                subTitle="Please enter the following information about your organization."
            >
                <form onSubmit={validateMethods.handleSubmit(formSubmitHandler)}>
                    <LInputField
                        label="Organization or Personal NPI Number (for US Based Healthcare Providers only)"
                        inputName="npiNumber"
                        placeholder="e.g. 1234567890"
                        defaultValue={organization.npiNumber?.toString()}
                        maxLength={10}
                        isNumber={true}
                    />
                    <LInputField
                        label="Organization name"
                        inputName="orgName"
                        placeholder="e.g. Sample Hospital"
                        defaultValue={organization.orgName}
                    />
                    <CountrySelectOptions
                        inputName="address.country"
                        value={
                            organization.address?.country
                                ? organization.address?.country === "United States"
                                    ? organization.address?.country
                                    : "Other"
                                : "United States"
                        }
                        otherCountry={organization.address.country}
                    />
                    <LInputField
                        label="Street address"
                        inputName="address.streetAddress"
                        placeholder="e.g. 123 Main St"
                        defaultValue={organization.address.streetAddress}
                    />
                    <LInputField
                        label="Apartment / Suite / Building (optional)"
                        inputName="address.streetAddress2"
                        defaultValue={organization.address.streetAddress2}
                    />
                    <LInputField label="City" inputName="address.city" defaultValue={organization.address.city} />
                    {country === "United States" ? (
                        <StateSelectOptions inputName="address.stateCode" value={organization.address.stateCode} />
                    ) : (
                        <LInputField
                            label="State / Province"
                            inputName="address.stateCode"
                            defaultValue={organization.address.city}
                        />
                    )}
                    <LInputField
                        label="ZIP / Postal Code"
                        inputName="address.postalCode"
                        placeholder="e.g. 90210"
                        maxLength={5}
                        defaultValue={organization.address.postalCode}
                    />
                    <LBtn fullWidth={true} type="submit">
                        Next
                    </LBtn>
                </form>
            </SignupContainer>
        </FormProvider>
    );
};
